/* eslint-disable @typescript-eslint/typedef */
import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Params, Router, RouterOutlet } from '@angular/router';
import { PopupService } from '@app/core/services/popup.service';
import { PermissionDirective } from '@app/features/admin/settings/permission/directives/permission.directive';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { SubtitleHeaderComponent } from '@app/shared/components/subtitle-header/subtitle-header.component';
import { PortReportTableConfig } from '@chartering/vessel-reports/vessel-reports.constants';
import { TranslocoModule, TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import {
  createAdvanceFilterConfig,
  ISharedAdvanceFilterConfig,
  SharedAdvanceFilterContainerComponent,
  SharedQueryAdvanceFilter
} from '@shared/components/filters';
import { TableComponent, TableConfig, TableTemplateDirective } from '@shared/components/table';
import { LIMIT_PER_PAGE_DEFAULT } from '@shared/shared.constants';
import { SafeAny } from '@shared/types/any.type';
import { SealFindByKeyPipe } from '@shipsfocus/seal';
import { ItineraryAction } from '@vessel-itinerary/state/itinerary.action';
import { PortReportDialogComponent } from '@vessel-reports/port/components/port-report-dialog/port-report-dialog.component';
import { PortActions } from '@vessel-reports/port/state/port.action';
import { PortService } from '@vessel-reports/port/state/port.service';
import { PortState } from '@vessel-reports/port/state/port.state';
import { VesselReportsAction } from '@vessel-reports/state/vessel-reports.action';
import { VesselReportsState } from '@vessel-reports/state/vessel-reports.state';
import { VesselReport, VesselReportPagination } from '@vessel-reports/state/vessel-reports.types';
import { DatetimeLocalPipe } from 'app/core/pipe/datetime/datetime-local.pipe';
import { ExcelService } from 'app/core/services/excel.service';
import { filterQueries } from 'app/core/utils/helpers';
import { isEmpty } from 'lodash';
import { debounceTime, forkJoin, from, map, merge, Observable, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { VesselReportsService } from '../state/vessel-reports.service';

@Component({
  selector: 'port',
  templateUrl: './port.component.html',
  styles: [
    `
      .port-grid {
        grid-template-columns: 150fr 200fr 150fr 311fr 200fr 200fr 150fr 24px;

        &-action {
          grid-template-columns: repeat(2, 1fr) 3.5rem 3rem;

          @screen sm {
            grid-template-columns: repeat(3, 1fr) 3.5rem 3rem;
          }

          @screen md {
            grid-template-columns: repeat(2, 1fr) 1.5fr repeat(2, 1fr) 3.5rem 3rem;
          }

          @screen lg {
            grid-template-columns: repeat(2, 1fr) 1.5fr repeat(3, 1fr) 1.5fr 3.5rem 3rem;
          }
        }
      }

      .mdc-list-item > span {
        width: auto !important;
      }
    `
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    NgClass,
    RouterOutlet,
    ReactiveFormsModule,
    TranslocoModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatProgressBarModule,
    MatSidenavModule,
    MatSortModule,
    MatTooltipModule,
    SealFindByKeyPipe,
    MatPaginator,
    SharedAdvanceFilterContainerComponent,
    TableComponent,
    TableTemplateDirective,
    PermissionDirective,
    MatProgressSpinner,
    SubtitleHeaderComponent
  ],
  providers: [DatetimeLocalPipe, SealFindByKeyPipe]
})
export class PortComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly translateService: TranslocoService = inject(TranslocoService);
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  @ViewChild('detailsDrawer', { static: true }) detailsDrawer: MatDrawer;
  @ViewChild(MatPaginator) private _paginator: MatPaginator;
  vesselReports: Signal<VesselReport[]> = computed(() => this.stateService.vesselReports());
  pagination: Signal<VesselReportPagination> = computed(() => this.stateService.pagination());
  selectedPortReport: WritableSignal<VesselReport> = signal(void 0);
  drawerType: WritableSignal<'view' | 'search'> = signal(void 0);
  queryParams: WritableSignal<Params> = signal(void 0);

  selectedDateFilter: Signal<string | null> = computed(() => this.stateService.etaFilter());
  protected readonly downloadingExcel: Signal<boolean> = this._excelService.listenToJob('vessel_report', 'si_port');

  currentStatusIndex: string;
  vesselReportsLoading: boolean = false;
  portReportPopupLoaded: boolean = false;

  statuses = [
    {
      id: 1,
      name: 'Draft',
      type: 'warning'
    },
    {
      id: 2,
      name: 'Saved',
      type: 'success'
    },
    {
      id: 3,
      name: 'Submitted',
      type: 'info'
    }
  ];

  reportTypes = [
    { id: 6, name: 'ARRIVAL', color: 'text-red-600 border-red-600' },
    { id: 8, name: 'DEPARTURE', color: 'text-blue-700 border-blue-700' }
  ];

  protected readonly advanceFilterConfig: ISharedAdvanceFilterConfig = createAdvanceFilterConfig(
    [
      {
        key: 'port_report_type_id',
        filterKey: 'filter[report_type_id]',
        titleKey: 'ship_interface.type',
        customClasses: 'hidden md:block',
        menuType: 'single-selection',
        selectionType: 'id',
        preload: true,
        items: computed(() => this._portState.types())
      },
      {
        key: 'date_report',
        filterKey: 'filter[date_report]',
        titleKey: 'ship_interface.report_date',
        menuType: 'date-range',
        customClasses: 'hidden md:block'
      },
      {
        key: 'voyage_number',
        filterKey: 'filter[voyage_number]',
        titleKey: 'ship_interface.voy',
        selectionType: 'voyage_number',
        displayName: 'voyage_number',
        customClasses: 'hidden md:block',
        items: computed(() => this._portState.voyages())
      },
      {
        key: 'port_name',
        filterKey: 'filter[port_id]',
        titleKey: 'ship_interface.port',
        selectionType: 'id',
        displayName: 'name',
        customClasses: 'hidden md:block',
        items: computed(() => this._portState.ports())
      }
    ],
    this.queryParams,
    ['filter[report_type_id]']
  );

  protected readonly tableConfig: TableConfig<VesselReport> = {
    // template: [
    //   'lg:grid-cols-[150fr_200fr_150fr_311fr_200fr_200fr_150fr_48px]',
    //   'md:grid-cols-[150fr_200fr_150fr_311fr_150fr_48px]',
    //   'sm:grid-cols-[200fr_150fr_311fr_48px]',
    //   'grid-cols-[200fr_311fr_48px]'
    // ],
    controller: true,
    header: {
      classes: 'sticky top-0 z-10 bg-white',
      sort: {}
    },
    body: {
      byRow: (row) => {
        return {
          classes: computed(
            () =>
              `hover:bg-slate-50 hover:cursor-pointer dark:hover:bg-hover ${this.selectedPortReport()?.id === row.id ? 'bg-primary-100 dark:bg-primary-900' : ''}`
          )
        };
      },
      permission: ['port_report_ship_interface', ['view']],
      onClick: (row) => {
        this.selectReport(row.id);
      }
    },
    columns: [
      {
        key: 'type',
        header: {
          value: 'ship_interface.type',
          classes: 'hidden md:block'
        },
        body: {
          classes: 'hidden md:block w-full overflow-hidden',
          wrapper: {
            by: 'type',
            classes: '!max-w-full'
          },
          value: (row) => computed(() => this.reportTypes.find((report) => report.id === row.report_type_id)?.name || '-')
        },
        controller: {
          label: 'ship_interface.timesheet_type',
          template: {
            md: '150fr'
          }
        }
      },
      {
        key: 'date_report',
        header: {
          value: 'ship_interface.report_date'
        },
        body: {
          value: (row) =>
            row.date_report
              ? this._datetimeLocal.transform(row.date_report, {
                  format: 'DD MMM YYYY'
                })
              : '-'
        },
        controller: {
          label: 'ship_interface.report_date',
          disabled: true,
          template: {
            default: '200fr'
          }
        }
      },
      {
        key: 'voyage_number',
        header: {
          value: 'ship_interface.voyage_number',
          classes: 'hidden sm:block'
        },
        body: {
          value: (row) => row.voyage_number || '',
          classes: 'hidden sm:block'
        },
        controller: {
          label: 'ship_interface.voyage_number',
          template: {
            sm: '150fr'
          }
        }
      },
      {
        key: 'current_port_name',
        header: {
          value: 'ship_interface.port'
        },
        body: {
          value: (row) => row.current_port_name || '-'
        },
        controller: {
          label: 'ship_interface.port',
          disabled: true,
          template: {
            default: '311fr'
          }
        }
      },
      {
        key: 'datetime_arrival',
        header: {
          value: 'ship_interface.est_arrival',
          classes: 'hidden lg:block'
        },
        body: {
          value: (row) =>
            row.datetime_arrival
              ? this._datetimeLocal.transform(row.datetime_arrival, {
                  format: 'HH:mm DD MMM YYYY'
                })
              : '-',
          classes: 'hidden lg:block'
        },
        controller: {
          label: 'ship_interface.est_arrival',
          template: {
            lg: '200fr'
          }
        }
      },
      {
        key: 'datetime_departure',
        header: {
          value: 'ship_interface.departure',
          classes: 'hidden lg:block'
        },
        body: {
          value: (row) =>
            row.datetime_departure
              ? this._datetimeLocal.transform(row.datetime_departure, {
                  format: 'HH:mm DD MMM YYYY'
                })
              : '-',
          classes: 'hidden lg:block'
        },
        controller: {
          label: 'ship_interface.departure',
          template: {
            lg: '200fr'
          }
        }
      },
      {
        key: 'status',
        header: {
          value: 'ship_interface.status',
          classes: 'hidden md:block'
        },
        body: {
          value: (row) => this._sealFindByKey.transform(row.status_id, 'id', this.statuses)?.name,
          wrapper: {
            by: 'status',
            type: (row) => this._sealFindByKey.transform(row.status_id, 'id', this.statuses)?.type
          },
          classes: 'hidden md:block'
        },
        controller: {
          label: 'ship_interface.status',
          template: {
            md: '150fr'
          }
        }
      },
      {
        key: 'delete',
        header: {
          classes: 'w-12'
        },
        body: {
          classes: 'w-12 h-12',
          wrapper: {
            by: 'button-icon',
            icon: {
              value: 'heroicons_outline:trash',
              classes: 'icon-size-5'
            }
          },
          permission: ['port_report_ship_interface', ['delete']],
          ignoreFromRowClick: true,
          onClick: (row) => this.deleteReport(row)
        },
        controller: {
          admin: true,
          template: '48px'
        }
      }
    ],
    render: 'grid'
  };
  protected readonly searchInputControl: UntypedFormControl = this.advanceFilterConfig.search.control;

  protected readonly queryAdvanceFilter: SharedQueryAdvanceFilter = (key: string, filterKey: string) => {
    return this._portService
      .getAdvanceFilters(key, 300, {
        ...filterQueries(this.queryParams(), filterKey),
        ...(filterKey === 'filter[report_type_id]' ? {} : !this.queryParams()?.['filter[report_type_id]'] ? { 'filter[report_type_id]': '6,8' } : {})
      })
      .pipe(
        map(({ data }: { data: SafeAny[] }) => {
          if (['port_report_type_id'].includes(key)) {
            return (data || []).map((item) => ({
              ...item,
              id: item.id.toString()
            }));
          } else if (['vessel_name', 'port_name'].includes(key)) {
            return data || [];
          } else if (['voyage_number'].includes(key)) {
            return (data || [])?.filter((item) => !!item.voyage_number);
          }

          return [];
        }),
        tap((response) => {
          if (key === 'port_report_type_id') {
            this._portState.set({
              types: response
            });
          } else if (key === 'voyage_number') {
            this._portState.set({
              voyages: response
            });
          } else if (key === 'port_name') {
            this._portState.set({
              ports: response
            });
          }
        })
      );
  };

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef,
    private _popupService: PopupService,
    private _actionService: VesselReportsAction,
    public stateService: VesselReportsState,
    private _injector: Injector,
    private _excelService: ExcelService,
    private _vesselReportsService: VesselReportsService,
    private _profileState: ProfileState,
    private _portReportActions: PortActions,
    private _itineraryActions: ItineraryAction,
    private _matDialog: MatDialog,
    private _portState: PortState,
    private _portService: PortService,
    private _datetimeLocal: DatetimeLocalPipe,
    private _sealFindByKey: SealFindByKeyPipe
  ) {}

  ngOnInit(): void {
    this._subscribeToSource$(this._vesselReportsService.vesselReportsLoading$, (vesselReportsLoading) => {
      this.vesselReportsLoading = vesselReportsLoading;
    });

    this._subscribeToSource$(this._activatedRoute.paramMap, (paramMap) => {
      this.currentStatusIndex = paramMap.get('status');
    });

    effect(
      () => {
        this.selectedPortReport.set({ ...this._portState.detailPortReport() });

        if (this._portState.detailPortReport()) {
          this.drawerType.set('view');
        }
      },
      { injector: this._injector }
    );

    this._activatedRoute.queryParams.pipe(takeUntil(this._unsubscribeAll)).subscribe((queryParams) => {
      this.queryParams.set({ ...queryParams });

      if (isEmpty(this.queryParams())) {
        void this._router.navigate([], {
          queryParams: {
            'filter[report_type_id]': '6,8'
          },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });

    this.detailsDrawer.openedChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((opened) => {
      if (!opened) {
        this.selectedPortReport.set(null);
        this.drawerType.set(null);
      }
    });
  }

  ngAfterViewInit(): void {
    try {
      if (!this._paginator) {
        return;
      }
      const searchChange$ = this.searchInputControl.valueChanges.pipe(
        debounceTime(500),
        tap(() => (this._paginator.pageIndex = 0)),
        takeUntil(this._unsubscribeAll)
      );

      merge(this._paginator.page, searchChange$)
        .pipe(
          switchMap(() => {
            const queryParams = {
              ...this.queryParams(),
              'filter[search]': this.searchInputControl.value || ''
            };

            return this._router.navigate(['./'], {
              relativeTo: this._activatedRoute,
              queryParams: {
                ...queryParams,
                page: this._paginator.pageIndex + 1,
                limit: this._paginator.pageSize
              }
            });
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => {
          let params = {};
          if (!this.queryParams()?.['filter[report_type_id]']) {
            params = {
              'filter[report_type_id]': '6,8'
            };
          }
          this._actionService.actions.getVesselReports({
            type: this.currentStatusIndex,
            queryParams: {
              ...this.queryParams(),
              ...params
            }
          });
        });
    } catch (error) {
      console.error(error);
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  backdropClicked(): void {
    if (this.drawerType() === 'view') {
      this._router.navigate(['./'], { relativeTo: this._activatedRoute, queryParams: this.queryParams() }).then();
    }

    if (this.drawerType() === 'search') {
      this.detailsDrawer?.close()?.then();
    }

    this._changeDetectorRef.markForCheck();
  }

  selectReport(id: any): void {
    this._portReportActions.actions.cleanState();
    this._portReportActions.actions.getDetailPortReport({ report_id: id });
    forkJoin([this._portReportActions.actions.getDetailPortReportSuccess$.pipe(take(1))])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.portReportPopupLoaded) {
          return;
        }
        const dialog = this._matDialog.open(PortReportDialogComponent, {
          data: {
            type: 'details'
          }
        });
        this.portReportPopupLoaded = true;
        dialog
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this._portState.set({
              detailPortReport: null
            });
            this.portReportPopupLoaded = false;
          });
      });
  }

  createReport(): void {
    if (this.portReportPopupLoaded) {
      return;
    }
    const dialog = this._matDialog.open(PortReportDialogComponent, {});
    this.portReportPopupLoaded = true;
    dialog
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.portReportPopupLoaded = false;
      });
  }

  exportPortReportsToExcel(): void {
    const queryParams = filterQueries(this.queryParams());
    this._vesselReportsService
      .getExcelPortReports(queryParams)
      .pipe(
        take(1),
        switchMap((excelData: VesselReport[]) =>
          from(
            this._excelService.generateExcel(
              [
                {
                  title: this.translateService.translate('ship_interface.port_report'),
                  tabColor: '#4FC3F7',
                  header: {
                    font: { size: 12, bold: true, color: '#FFFFFF' },
                    alignment: { horizontal: 'center' },
                    fill: { pattern: 'solid', color: '#3949AB' }
                  },
                  table: {
                    config: PortReportTableConfig,
                    data: excelData?.map((item: VesselReport) => ({
                      ...item,
                      report_type_id: this.reportTypes.find((type) => type?.id === item?.report_type_id)?.name,
                      status_id: this.statuses.find((status) => status?.id === item?.status_id).name
                    }))
                  },
                  uppercase: true
                }
              ],
              `Vessel_Report_Port`
            )
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  deleteReport(report: VesselReport): void {
    this._popupService.openConfirmationDialog({
      title: this.translateService.translate('ship_interface.delete_report'),
      message: this.translateService.translate('ship_interface.are_you_sure_you_want_to_delete_this_report_this_action_cannot_be_undone'),
      actionType: 'delete',
      confirmButtonColor: 'warn',
      dismissible: true,
      onAccept: () => {
        this._actionService.actions.deleteVesselReport({
          id: report.id,
          type: this.currentStatusIndex,
          activatedRoute: this._activatedRoute
        });
        this._actionService.actions.deleteVesselReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
          this._actionService.actions.getVesselReports({
            type: this.currentStatusIndex,
            queryParams: this.queryParams()
          });
        });
      },
      onCancel: () => {}
    });
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  navigateWithParams(additionalParams = {}): void {
    this._router
      .navigate(['./'], {
        relativeTo: this._activatedRoute,
        queryParams: {
          page: '1',
          limit: this.queryParams()?.['limit'] || LIMIT_PER_PAGE_DEFAULT,
          sort: this.queryParams()?.['sort'] || '',
          ...additionalParams
        }
      })
      .then(() => {
        let params = {};
        if (!this.queryParams()?.['filter[report_type_id]']) {
          params = {
            'filter[report_type_id]': '6,8'
          };
        }
        this._actionService.actions.getVesselReports({
          type: this.currentStatusIndex,
          queryParams: {
            ...this.queryParams(),
            ...params
          }
        });
      });
  }

  toggleSidebar(): void {
    this.stateService.set({ openedSidebarDrawer: !this.stateService.openedSidebarDrawer() });
  }

  private _subscribeToSource$<T>(source$: Observable<T>, callback: (value: T) => void): void {
    source$.pipe(takeUntil(this._unsubscribeAll)).subscribe((value: T) => {
      callback(value);

      this._changeDetectorRef.markForCheck();
    });
  }

  exportToExcel(): void {
    if (this.downloadingExcel()) {
      return;
    }
    this._excelService.triggerJobFlowAsync('vessel_report', this.queryParams(), 'si_port');
  }
}
